import {Component, EventEmitter, inject, Input, OnInit, Output, ViewChild} from '@angular/core';
import {UserMetricInstance} from "../../../../models/user/Metric/UserMetricInstance";
import {BaseComponentWithDrawer} from "../../../registry/base-component-with-drawer.component";
import {DashboardService} from "../../../../services/dashboard/dashboard.service";
import {DxDataGridComponent, DxFormComponent} from "devextreme-angular";
import {UserMetricGroup} from "../../../../models/user/Metric/UserMetricGroup";
import {PutUserMetricGroupCB, UserMetricDTO} from "../../../../ope/user/metric/PutUserMetricGroupCB";
import {DataTypeGroupEnum} from "../../../../enums/dashboard/DataTypeUnitEnum";

@Component({
  selector: 'app-chart-form-edit',
  templateUrl: './chart-form-edit.component.html',
  styleUrls: ['./chart-form-edit.component.css', '../../../common/drawer-style.css']
})
export class ChartFormEditComponent extends BaseComponentWithDrawer implements OnInit {

  /**
   * Event that tells the father component to destroy this component and save the chart (if the user wants to)
   */
  @Output('drawerClosed') drawerClosed: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Reference to the Metric Instance table HTML component
   */
  @ViewChild('metricsTable', {static: false}) metricsTable!: DxDataGridComponent;

  /**
   * Reference to the form HTML component
   */
  @ViewChild('editGroupForm', {static: false}) editGroupForm!: DxFormComponent;

  /**
   * Contains the selected user metric group.
   */
  @Input('userMetricGroup') userMetricGroup?: UserMetricGroup;
  /**
   * Boolean flag used to store the validity of the form
   * @protected
   */
  public isFormValid: boolean;
  /**
   * Boolean flag used to control the edit mode. When it's false no data can be changed
   * @protected
   */
  public editMode: boolean;
  nameInputFieldOptions = {
    valueChangeEvent: 'keyup',
  };
  /**
   * List containing all the metrics that were added in the current user metric group
   * @protected
   */
  protected selectedMetricIds: number[];
  /**
   * List containing all the metrics that were added in the current user metric group
   * @protected
   */
  protected selectedMetrics: UserMetricInstance[];
  /**
   * Copy of the currently selected user metric group, used for the form data
   * @protected
   */
  protected copiedUserMetricGroup: UserMetricGroup;
  /**
   * Service containing the shared data and procedures among dashboard components
   * @protected
   */
  protected dashboardService = inject(DashboardService);
  /**
   * Index of the currently selected tab
   * @protected
   */
  protected selectedTab = 0;
  protected readonly DataTypeGroupEnum = DataTypeGroupEnum;

  constructor() {

    super();
    this.copiedUserMetricGroup = new UserMetricGroup();
    if (!!this.userMetricGroup) {
      this.copiedUserMetricGroup = this.appService.deepCopy(this.userMetricGroup);
    }
    this.isFormValid = true;
    this.editMode = false;
    this.selectedMetrics = [];
    this.selectedMetricIds = [];
    if (!!this.userMetricGroup) {
      this.populateSelectedMetrics(this.userMetricGroup);
    }
  }

  public loadFormData(parameterMetricGroup?: any) {
    this.copiedUserMetricGroup = new UserMetricGroup();
    if (!!parameterMetricGroup) {
      this.copiedUserMetricGroup = this.appService.deepCopy(parameterMetricGroup);
    } else if (!!this.userMetricGroup) {
      this.copiedUserMetricGroup = this.appService.deepCopy(this.userMetricGroup);
    }
    this.isFormValid = true;
    this.editMode = false;
    this.selectedMetrics = [];
    this.selectedTab = 0;
    this.populateSelectedMetrics(this.copiedUserMetricGroup);
    this.selectedMetricIds = this.selectedMetrics.map(x => x.id);
  }

  public resetFormData() {
    this.copiedUserMetricGroup = new UserMetricGroup();
    this.userMetricGroup = undefined;
  }

  /**
   * If the user wants to save his changes this method calls the putUserMetricGroup API to persist the changes on the database.
   * after doing that it calls the getUserMetricGroups method to update the list of the UserMetricGroups
   *
   * If the user wants to discard his changes it reverts all the changed data back to its starting point.
   *
   * @param result True if the user wants to save, False if the user wants to discard changes
   */
  public saveChanges(result: boolean) {
    if (!!this.userMetricGroup) {
      if (result) {
        let cb = new PutUserMetricGroupCB();
        cb.i.id = this.userMetricGroup.id;
        cb.i.bucketValue = this.copiedUserMetricGroup.bucketValue;
        cb.i.bucketUnits = this.copiedUserMetricGroup.bucketUnits;
        cb.i.metrics = [];
        this.selectedMetrics?.forEach(mi => {
          let newUM = new UserMetricDTO();
          newUM.id = mi.id;
          newUM.chartType = mi.chartType;
          newUM.functionType = mi.functionType;
          newUM.color = mi.color;
          cb.i.metrics!.push(newUM);
        });
        cb.i.bucketValue = this.copiedUserMetricGroup.bucketValue;
        cb.i.name = this.copiedUserMetricGroup.name;
        this.drawerClosed.emit(cb);
      } else {
        this.copiedUserMetricGroup = this.appService.deepCopy(this.userMetricGroup);
        this.selectedMetrics = [];
        this.populateSelectedMetrics(this.userMetricGroup);
      }
    }
  }

  /**
   * Check if new group is valid, to enable SAVE button
   */
  checkGroupValidation() {
    this.editMode = this.copiedUserMetricGroup.name !== this.userMetricGroup?.name;
    const result = this.editGroupForm.instance.validate();
    this.isFormValid = !!result.isValid;
  }

  populateSelectedMetrics(userMetricGroup: UserMetricGroup) {
    if (!!userMetricGroup.metrics) {
      for (let mi of userMetricGroup.metrics) {
        let copyMi = this.appService.deepCopy(mi);
        this.selectedMetrics.push(copyMi);
      }
    }
  }
}
